/**
 * `RiskEmailScene` projections — the demo's `buildEmail(m)` (demo line 2595)
 * and its helpers `emailPeople` (2573), `emailActions` (2574), `emailBody`
 * (2582), `sigBlock` (1495) and `emlText` (2592).
 *
 * The weekly machine-risk email to one site's technical team: who it goes to,
 * the week's figures, the machines at risk, what the OEM needs from the site,
 * the parts below minimum and the drafts waiting on approval, signed by the
 * fleet reliability lead. Mount Isolde's is the email the programme actually
 * sent (`reports.json` → `email_mi`); every other site's is written live from
 * `mineWeek` with the same sections in the same order.
 *
 * ## Brand
 *
 * The recipients, the sender and their mobiles are the data pack's fictional
 * people and stay so. The **OEM** in the signature follows the selected brand:
 * the transform takes an {@link OemDocIdentity} and writes the legal name,
 * unit, operations line and web address from it, dropping any field the brand
 * does not carry. Under Meridian the text is the demo's, byte for byte.
 *
 * Pure: no React, no clock (the generation time is the pack's `06:05`), no
 * fetching.
 *
 * @see src/versions/v1/modules/reports/RiskEmailScene.tsx
 * @see docs/logic/reports/email.md
 */

import { BY_ID, ENERGY_FACTORS, FLEET, HEALTH, IDS, MINES } from '../../data/core';
import type { MineCode } from '../../schema/machine';
import { CONDITION_BAND_LABEL } from '../../schema/machine';
import type { PersonRecord } from '../../schema/person';
import type { EmailMessage, RiskEmailPack, RiskEmailReport } from '../../schema/report';
import {
  CUSTOMER_ORGS,
  DATA_OEM_ORG,
  OEM_MOBILES,
  type OemDocIdentity,
} from '../../shared/docs/doc-identity';
import { groupNum, inWk, mineWeek, WK, type MineWeek } from '../../shared/oem-engine';
import type { InventoryCtx, ShiftCtx, WorkOrderCtx } from '../../shared/oem-engine';
import {
  REPORT_DOWNLOAD,
  REPORT_FILE_DATE,
  REPORT_FORMAT,
  REPORT_GENERATED_AT,
  reportSlug,
} from '../../shared/report-catalog';

/** What the email reads. */
export interface RiskEmailCtx extends WorkOrderCtx, ShiftCtx, InventoryCtx {
  readonly people: readonly PersonRecord[];
  /** `reports.email_mi` — Mount Isolde's email as sent. */
  readonly pack: RiskEmailPack;
}

/** Recipients and sender. */
export interface EmailPeople {
  readonly frm: PersonRecord;
  readonly to: readonly PersonRecord[];
  readonly cc: readonly PersonRecord[];
}

const firstName = (name: string): string => name.split(' ')[0];

/** Demo 2573. `emailPeople(m)`. Field technicians go last on the To line. */
export function emailPeople(m: MineCode, people: readonly PersonRecord[]): EmailPeople {
  const frm = people.find((p) => p.role === 'Fleet Reliability Lead') ?? people[0];
  const isTech = (p: PersonRecord): number => (/Technician/.test(p.role) ? 1 : 0);
  return {
    frm,
    to: people
      .filter((p) => p.mine === m && /Superintendent|Planner|Field Service Technician/.test(p.role))
      .slice()
      .sort((a, b) => isTech(a) - isTech(b)),
    cc: people.filter(
      (p) =>
        p.role === 'Regional Service Manager' ||
        (p.mine === m && /Reliability Engineer|Seismic/.test(p.role)),
    ),
  };
}

/** Demo 1495. `sigBlock(p)` — five signature lines, with the OEM from the brand. */
export function sigBlock(p: PersonRecord, oem: OemDocIdentity): string[] {
  if (p.org === DATA_OEM_ORG) {
    const site =
      (
        {
          KG: 'Kalbarri Gold site',
          BR: 'Broken Ridge site',
          MI: 'Mount Isolde site',
          OH: 'Ochre Hill site',
          GD: 'Gawler Deep site',
          MR: 'Mount Read site (Burnie)',
        } as Record<string, string>
      )[p.mine] ?? oem.hqShort;
    const mob = OEM_MOBILES[p.name];
    const phone = [
      mob && !mob.startsWith('1800') ? `M ${mob}` : null,
      oem.opsCentre ? `Operations Centre ${oem.opsCentre} (24/7)` : null,
    ].filter(Boolean);
    return [
      p.name,
      [p.role, site].filter(Boolean).join(' · '),
      `${oem.legal} · ${oem.unit}`,
      ...(phone.length ? [phone.join(' · ')] : []),
      [p.email, oem.web].filter(Boolean).join(' · '),
    ];
  }
  const o = CUSTOMER_ORGS[p.org];
  return o
    ? [p.name, `${p.role} · ${o.unit}`, `${o.legal} · ${o.addr}`, `T ${o.phone}`, p.email]
    : [p.name, p.role, p.email];
}

/** One "what we need from you" line. */
export interface EmailAction {
  readonly machine: string;
  readonly action: string;
}

/** Demo 2574–2581. `emailActions(m, mw)` — at most six, site-specific ones last. */
export function emailActions(m: MineCode, mw: MineWeek, ctx: RiskEmailCtx): EmailAction[] {
  if (m === 'MI') return ctx.pack.actions.map((a) => ({ machine: a.machine, action: a.action }));

  const acts: EmailAction[] = [];
  for (const r of mw.flagged.slice(0, 3)) {
    const d = mw.drafts.find((w) => w.machine_id === r.machine_id);
    if (d) {
      const what = d.description
        .replace(/^AGENT-DRAFTED[^:]*:\s*/, '')
        .split('. ')[0]
        .replace(/\.$/, '');
      acts.push({ machine: r.machine_id, action: `Approve ${d.wo_id} — ${what}.` });
    } else {
      const when = r.band !== 'poor' ? 'before the next shift' : 'at the next PM';
      acts.push({
        machine: r.machine_id,
        action: `${r.worst} — inspect ${when} (condition ${r.score}/100). ${r.note}`,
      });
    }
  }

  const weekRows = (mine: MineCode) =>
    ctx.shiftDaily.filter((s) => BY_ID[s.machine_id]?.mine === mine && inWk(s.date));

  if (m === 'KG') {
    acts.push({
      machine: IDS.L14,
      action:
        'Follow-up hydraulic oil sample at +50 h after the flush (≈ 11 Sep); target ISO 18/16/13. Sample every 250 h until clean (TB-2026-031).',
    });
    const abuse = weekRows('KG').reduce((a, s) => a + s.abuse, 0);
    acts.push({
      machine: 'Night B',
      action: `${abuse} bucket-ramming / abuse events this week — supervisor review; the L-02 coupling failure was practice, not parts (CS-2026-2210 declined).`,
    });
    if (mw.late) {
      acts.push({
        machine: 'All',
        action: `${mw.late} PMs ran late since 1 Aug — hold the weekend PM window; late PMs are the strongest predictor of breakdown cost at this site.`,
      });
    }
  }
  if (m === 'BR') {
    acts.push({
      machine: IDS.J07,
      action:
        'Approve WO-26-11901 and SO-90418 (air freight from Perth, 3 days) so the front-end change lands at the next cut change; move J-04 off the 1840 rehab drives until the kit arrives.',
    });
    acts.push({
      machine: 'L-07',
      action:
        'Battery-electric loader: module 7 lands 09 Sep PM (SO-90427) — keep the single-shift duty and do not override the fast-charge lockout until the HV isolation test passes.',
    });
  }
  if (m === 'OH') {
    acts.push({
      machine: 'S-04',
      action:
        "Approve WO-26-11913 — hammer exchange at Thursday's pattern change; the reserved hammer is the only one in the NT, so the Darwin replenishment (SO-90430) matters.",
    });
    acts.push({
      machine: 'All strings',
      action:
        'Thread-gauge every pipe at the next PM-250 and make up to 8.5 kN·m from the wrench readout (TB-2026-033, SP-MS-PIPE-007) — the S-05 fishing job cost A$24.3k against A$4.6k for a planned pipe change.',
    });
  }
  if (m === 'GD') {
    acts.push({
      machine: 'S-09',
      action:
        'Approve WO-26-11914 and SO-90431 (rotary head motor from Adelaide, road, Thursday); keep the rig on the pre-split row with feed force backed off.',
    });
    acts.push({
      machine: 'L-17 + ML-21 fleet',
      action:
        "Approve the hose campaign starting with WO-26-11916 — four ML-21 hose kits past the OEM 4,000 h interval; the contract plan had 6,000 h. L-18's fire cost A$98.9k.",
    });
    acts.push({
      machine: 'All loaders',
      action:
        'Hydraulic top-up above 5 L per shift is now a Watch condition on every loader, owner and contractor — please make sure the plods keep logging the litres.',
    });
  }
  if (m === 'MR') {
    acts.push({
      machine: 'L-33',
      action:
        'Approve WO-26-11917 and SO-90435 — charging connector change on Thursday with the part carried from Burnie; keep L-33 on bay 1 and take the charging interfaces off the wash-down route (TB-2026-034 card at every bay).',
    });
    acts.push({
      machine: 'J-17',
      action:
        'Cable-reel junction-box seal kit and cable end land Thursday (SO-90436); electrical test before return to service, and a breather on the box if it takes water a third time.',
    });
    const waits = weekRows('MR').filter(
      (s) => BY_ID[s.machine_id]?.battery && /CHARGE-WAIT|LOW-SOC/.test(s.delays || ''),
    ).length;
    acts.push({
      machine: 'ML-21E fleet',
      action: `Six loaders on three bays lost ${waits} shifts to charge waits or low-SoC starts this week — hold the staggered rotation; the fourth bay on the 500 level is the fix.`,
    });
    acts.push({
      machine: 'PM plan',
      action:
        'Load the Western Tiers contract PM plan beside the OEM plan so the interval gaps show per machine (the Gawler Deep lesson).',
    });
  }
  return acts.slice(0, 6);
}

/** The grid factor printed in the emissions line. */
const gridFactor = (m: MineCode): number =>
  MINES[m].grid_kgco2_kwh ?? ENERGY_FACTORS.find((x) => x.mine === m)?.grid_kgco2_per_kwh ?? 0;

/**
 * Demo 2582–2591. `emailBody(m, mw)`. Mount Isolde's is the sent text with its
 * signature re-issued under the selected brand; every other site's is built.
 */
export function emailBody(
  m: MineCode,
  mw: MineWeek,
  ppl: EmailPeople,
  ctx: RiskEmailCtx,
  oem: OemDocIdentity,
): string {
  const sig = sigBlock(ppl.frm, oem);
  if (m === 'MI') {
    // The sent body ends with a blank line and the five-line signature.
    const cut = ctx.pack.body.lastIndexOf('\n\n');
    return `${cut > 0 ? ctx.pack.body.slice(0, cut) : ctx.pack.body}\n\n${sig.join('\n')}`;
  }

  const M = MINES[m];
  const L: string[] = [];
  L.push(
    `${ppl.to.map((p) => firstName(p.name)).join(', ')},`,
    '',
    `Weekly machine risk summary for ${M.name}, ${WK.short}. Generated from the fleet workspace at ` +
      `${REPORT_GENERATED_AT} today; every figure links back to a work order, a shift record or the ` +
      `${M.data_mode === 'Live' ? 'live feed' : 'Pitram shift-end export'} if you want to check it.`,
    '',
    'FLEET THIS WEEK',
    `- Availability ${mw.avail}% across ${mw.n} machines (engine hours / scheduled operating hours, 9.5 h per shift)`,
    `- ${mw.breakdowns} breakdown work orders, ${mw.reactive} reactive in total; A$${groupNum(mw.cost)} all-in cost this week (direct + lost production)`,
    `- Emissions ${mw.co2_t} t CO2-e (${groupNum(mw.diesel)} L diesel + ${groupNum(mw.kwh)} kWh at ${gridFactor(m)} kg/kWh)`,
    `- Condition bands: ${mw.bands.good} good (80-100), ${mw.bands.fair} fair (60-79), ${mw.bands.poor} poor (40-59), ${mw.bands.vpoor} very poor (20-39), ${mw.bands.major} major issue (<20)`,
    '',
    'MACHINES AT RISK',
  );
  for (const r of mw.flagged) {
    const band = r.band === 'poor' ? 'Poor' : CONDITION_BAND_LABEL[r.band].toUpperCase();
    L.push(`- ${r.machine_id} (${r.model}) — ${band}: ${r.worst} at condition ${r.score}/100. ${r.note}`);
  }
  if (!mw.flagged.length) L.push('- None in the poor, very poor or major-issue bands this week.');
  if (mw.fair.length) {
    L.push(
      `- Fair band, monitor only: ${mw.fair.map((r) => `${r.machine_id} (${r.worst} ${r.score}/100)`).join(', ')}`,
    );
  }

  L.push('', 'WHAT WE NEED FROM YOU');
  for (const a of emailActions(m, mw, ctx)) L.push(`- ${a.machine}: ${a.action}`);

  L.push('', `PARTS BELOW MINIMUM AT ${m} STORE`);
  for (const p of mw.low.slice(0, 8)) {
    L.push(`- ${p.part_no}: ${p.qty_on_hand} on hand, min ${p.min_level}`);
  }
  if (mw.low.length > 8) L.push(`- and ${mw.low.length - 8} more lines — full list in the parts portal`);
  if (!mw.low.length) L.push('- None.');

  const n = mw.drafts.length;
  L.push(
    '',
    n
      ? `${n} agent-drafted work order${n > 1 ? 's are' : ' is'} waiting for approval in the workspace (${mw.drafts
          .map((w) => w.wo_id)
          .join(', ')}). Nothing has been actioned on the machines.`
      : 'No agent drafts are waiting on this site. Nothing has been actioned on the machines.',
    '',
    ...sig,
  );
  return L.join('\n');
}

/**
 * Demo 2592. `emlText(e)` — an RFC 822 message a mail client opens as a draft.
 * The date is the snapshot's morning at the generation time.
 */
export function emlText(e: EmailMessage): string {
  const addr = (p: { name: string; email: string }) => `${p.name} <${p.email}>`;
  const hdr = [
    `From: ${addr(e.from)}`,
    `To: ${e.to.map(addr).join(', ')}`,
    `Cc: ${e.cc.map(addr).join(', ')}`,
    `Subject: ${e.subject}`,
    `Date: Wed, 09 Sep 2026 ${REPORT_GENERATED_AT}:00 +1000`,
    'X-Generated-By: ProjeX OEM+ fleet workspace',
    'Content-Type: text/plain; charset="utf-8"',
    'MIME-Version: 1.0',
    '',
  ];
  return `${hdr.join('\n')}\n${e.body}\n`;
}

/** Demo 2595–2600. `buildEmail(m)`, as data. */
export function riskEmailReport(
  m: MineCode,
  ctx: RiskEmailCtx,
  oem: OemDocIdentity,
): RiskEmailReport {
  const mw = mineWeek(m, ctx);
  const ppl = emailPeople(m, ctx.people);
  const subject = m === 'MI' ? ctx.pack.subject : `${mw.name} — machine risk summary and actions, ${WK.short}`;
  const fileName = `Risk-Summary-Email_${reportSlug(mw.name)}_${REPORT_FILE_DATE}.eml`;
  const address = (p: PersonRecord) => ({ name: p.name, email: p.email });
  return {
    kind: 'email',
    mine: m,
    title: `Machine risk summary · ${mw.name}`,
    sub: `Email to the site technical team · ${WK.short}${m !== 'MI' ? ` · built live for ${mw.name}` : ''}`,
    docId: fileName.replace(/\.eml$/, ''),
    format: REPORT_FORMAT.email,
    download: REPORT_DOWNLOAD.email,
    fileName,
    email: {
      from: address(ppl.frm),
      to: ppl.to.map(address),
      cc: ppl.cc.map(address),
      subject,
      body: emailBody(m, mw, ppl, ctx, oem),
    },
    senderOrg: ppl.frm.org,
    availability: mw.avail,
    flaggedIds: mw.flagged.map((f) => f.machine_id),
    machineIds: mw.machines.map((x) => x.machine_id),
    sources: [
      {
        label: 'Condition register',
        dataset: 'health',
        count: `${HEALTH.filter((h) => BY_ID[h.machine_id]?.mine === m).length} rows`,
      },
      { label: 'Shift records', dataset: 'shift', count: `${mw.days} machine-days` },
      { label: 'SAP PM work orders', dataset: 'wos', count: `${mw.wos} this week` },
      { label: 'Inventory snapshot', dataset: 'inv', count: `${mw.low.length} below min` },
      { label: 'Energy factors', dataset: 'efac', count: 'diesel 2.68 kg/L' },
    ],
  };
}

/** The site's machines — for the shift-record source link. */
export const siteMachineIds = (m: MineCode): string[] =>
  FLEET.filter((f) => f.mine === m).map((f) => f.machine_id);
